import { useEffect, useRef, useState } from 'react'

/**
 * Комната Jitsi через iframe External API. Общая для консоли
 * и водителя: правка поведения комнаты нужна обоим приложениям
 * одновременно — значит, её место в пакете (правило монорепозитория).
 *
 * Скрипт external_api.js грузится С ХОСТА ВИДЕОСЕРВЕРА, а не бандлится:
 * версия API обязана совпадать с версией развёрнутого стека, и это
 * единственный способ не разъехаться (плюс бюджеты сборки и isolation
 * не страдают). URL приходит из ответа /token — фронт не знает адрес
 * meet заранее и не обязан.
 *
 * props:
 *   token      — { token, url } из /token или /guest-token
 *   subject    — название звонка (заголовок конференции)
 *   guest      — урезанная панель кнопок
 *   roomConfig — { chat_enabled, screenshare, start_muted_after }
 *                из настроек модуля (сотрудник) или guest_view (гость)
 *   lobby      — включить лобби после входа (модератор; клейм — в JWT)
 *   onClose    — конференция закрыта (readyToClose / hangup)
 *   onApiReady — наружу отдаётся api для кнопок модератора
 */

const loaded = new Map() // origin -> Promise<void>

function loadExternalApi(origin) {
  if (window.JitsiMeetExternalAPI) return Promise.resolve()
  if (!loaded.has(origin)) {
    loaded.set(
      origin,
      new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = `${origin}/external_api.js`
        script.async = true
        script.onload = () => resolve()
        script.onerror = () =>
          reject(new Error('Не удалось загрузить видеосервер'))
        document.head.appendChild(script)
      })
    )
  }
  return loaded.get(origin)
}

const STAFF_BUTTONS = [
  'microphone', 'camera', 'desktop', 'chat', 'raisehand',
  'participants-pane', 'tileview', 'toggle-camera', 'settings', 'hangup',
  // breakout — комнаты для планёрок; кнопку видит модератор,
  // Jitsi сам прячет её у остальных.
  'breakout-rooms',
]
const GUEST_BUTTONS = [
  'microphone', 'camera', 'desktop', 'chat', 'raisehand', 'tileview', 'hangup',
]

export function JitsiRoom({
  token, subject, guest = false, roomConfig = {}, lobby = false,
  onClose, onApiReady,
}) {
  const containerRef = useRef(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!token?.token || !token?.url || !containerRef.current) return undefined

    let api = null
    let disposed = false
    const url = new URL(token.url)
    const roomName = url.pathname.replace(/^\//, '')

    // Кнопки собираются из настроек модуля: выключенный владельцем
    // чат или скриншеринг не рисуются вовсе, а не серые.
    let buttons = guest ? [...GUEST_BUTTONS] : [...STAFF_BUTTONS]
    if (roomConfig.chat_enabled === false) buttons = buttons.filter((b) => b !== 'chat')
    if (roomConfig.screenshare === false) buttons = buttons.filter((b) => b !== 'desktop')
    // Запись появляется только если владелец включил модульную
    // настройку (и стек Jibri развёрнут — иначе Jitsi скажет сам).
    if (!guest && roomConfig.recording === true) buttons = [...buttons, 'recording']

    loadExternalApi(url.origin)
      .then(() => {
        if (disposed) return
        // Протокол видеосервера. external_api строит src iframe как
        // `${noSSL?'http':'https'}://host/room` (jitsi-meet#1836).
        // Если noSSL не true — уходит на https, которого на локальном
        // стенде нет → ERR_CONNECTION. Берём протокол и из адреса
        // комнаты, и из текущей страницы: достаточно любого http,
        // чтобы не уйти на несуществующий https.
        const isHttp = url.protocol === 'http:'
          || window.location.protocol === 'http:'
        api = new window.JitsiMeetExternalAPI(url.host, {
          roomName,
          jwt: token.token,
          parentNode: containerRef.current,
          // Растянуть на весь контейнер: external_api иначе создаёт
          // iframe фиксированного размера (узкая полоска).
          width: '100%',
          height: '100%',
          // Оба написания — разные версии external_api.js.
          noSSL: isHttp,
          noSsl: isHttp,
          // Пре-экран уже пройден на нашей странице.
          configOverwrite: {
            prejoinConfig: { enabled: false },
            disableDeepLinking: true,
            subject: subject || undefined,
            // Заходить с выключенными микрофоном и камерой: человек
            // сам включит их, когда готов (не «врывается» в звонок
            // с открытым микрофоном).
            startWithAudioMuted: true,
            startWithVideoMuted: true,
            // BOSH/serviceUrl здесь НЕ задаём: явный путь склеивается
            // с host в битый "https://http://.../http-bind"
            // (ERR_NAME_NOT_RESOLVED). Без них Jitsi строит BOSH сам
            // относительно адреса iframe (:8082, уже http после фикса
            // src ниже) — как при прямом заходе, где всё работает.
            ...(Number.isInteger(roomConfig.start_muted_after)
              && roomConfig.start_muted_after > 0
              ? { startAudioMuted: roomConfig.start_muted_after }
              : {}),
          },
          // ВНИМАНИЕ: отсюда применяются НЕ любые ключи interfaceConfig,
          // а только те, что перечислены в белом списке jitsi-meet
          // (react/features/base/config/interfaceConfigWhitelist.ts).
          // Всё остальное молча отбрасывается — без ошибки в консоли.
          //
          // Здесь оставлены только реально проходящие ключи. Настройки
          // водяных знаков (SHOW_JITSI_WATERMARK, JITSI_WATERMARK_LINK,
          // DEFAULT_LOGO_URL, APP_NAME) в белый список НЕ входят: они
          // задаются на стороне видеосервера, в
          // deploy/jitsi/branding/custom-interface_config.js. Не
          // возвращайте их сюда — работать не будет, а выглядит рабочим.
          interfaceConfigOverwrite: {
            TOOLBAR_BUTTONS: buttons,
            // Оба ключа в белом списке — подпись провайдера в интерфейсе.
            NATIVE_APP_NAME: 'Carflow',
            PROVIDER_NAME: 'Carflow',
            SHOW_POWERED_BY: false,
          },
        })

        // Разрешения устройств + фикс протокола. external_api в этой
        // версии ИГНОРИРУЕТ noSSL и строит src iframe как https://host,
        // которого на локальном HTTP-стенде нет → ERR_CONNECTION.
        // Перехватываем iframe сразу после создания и, если протокол
        // разошёлся с адресом видеосервера из токена, переписываем src.
        try {
          const frame = api.getIFrame?.()
          if (frame) {
            frame.allow = [
              'camera', 'microphone', 'display-capture',
              'autoplay', 'clipboard-write', 'speaker-selection',
            ].join('; ')
            // Размеры напрямую: external_api иногда ставит фиксированные
            // (узкая полоска). CSS .jitsi-room iframe дублирует на случай
            // пересоздания.
            frame.style.width = '100%'
            frame.style.height = '100%'
            frame.style.minHeight = '75vh'
            // Целевой протокол — из адреса видеосервера (token.url).
            // Если external_api построил src с другой схемой (https
            // вместо http), чиним, пока iframe не догрузился.
            if (frame.src && url.protocol === 'http:'
                && frame.src.startsWith('https://')) {
              frame.src = frame.src.replace(/^https:/, 'http:')
            }
          }
        } catch { /* getIFrame недоступен — не критично */ }

        api.on('readyToClose', () => onClose?.())
        if (lobby) {
          // Лобби включается модератором после входа: prosody не даёт
          // сделать это заранее — комнаты ещё не существует.
          api.on('videoConferenceJoined', () => {
            try { api.executeCommand('toggleLobby', true) } catch { /* нет прав */ }
          })
        }
        onApiReady?.(api)
      })
      .catch((e) => setError(e.message))

    return () => {
      disposed = true
      api?.dispose()
    }
    // Пересоздавать конференцию стоит ТОЛЬКО при смене токена:
    // subject/onClose/onApiReady меняются между рендерами, но не должны
    // размонтировать живой звонок.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token?.token, token?.url])

  if (error) {
    return (
      <div className="jitsi-room jitsi-room--error">
        <p>{error}</p>
        <p className="text-muted">Проверьте связь или обратитесь к администратору.</p>
      </div>
    )
  }

  return <div ref={containerRef} className="jitsi-room" />
}
